import { useEffect, useMemo, useRef } from "react";
import { getConversationTagsBatch } from "@api/services/facebook";
import { fetchAgentsByConversations } from "@features/agents/agentsSlice";
import { PLATFORMS, CHANNELS } from "@common";
import type { Conversation } from "@interfaces/chat.interface";
import type { AppDispatch } from "@stores/redux/store";
import type { ConversationTabType } from "@constants/conversationTabs";

type Page = { id: string; name: string };

type Params = {
  displayConversations: Conversation[];
  selectedFanpageName?: string;
  facebookPages: Page[];
  activeTab: ConversationTabType;
  updateConversationTagsMap: (tagsMap: Record<string, Array<{ description: string; id: number; tagName: string }>>) => void;
  dispatch: AppDispatch;
};

export const useConversationTagsAndAgents = ({
  displayConversations,
  selectedFanpageName,
  facebookPages,
  activeTab,
  updateConversationTagsMap,
  dispatch,
}: Params) => {
  const lastFetchedKeyRef = useRef<string>("");
  const lastFetchedAgentsKeyRef = useRef<string>("");

  const conversationsKey = useMemo(() => {
    const ids = displayConversations
      .filter((conv) => conv.channel === CHANNELS.FACEBOOK)
      .map((conv) => conv.lastMessage?.conversationId)
      .filter((id): id is string => Boolean(id))
      .sort();
    return JSON.stringify(ids);
  }, [displayConversations]);

  useEffect(() => {
    lastFetchedKeyRef.current = "";
    lastFetchedAgentsKeyRef.current = "";
    updateConversationTagsMap({});
  }, [activeTab, updateConversationTagsMap]);

  useEffect(() => {
    const fetchAllTags = async () => {
      if (!selectedFanpageName || displayConversations.length === 0) {
        updateConversationTagsMap({});
        return;
      }

      const selectedPage = facebookPages.find(
        (page) => page.name === selectedFanpageName
      );

      if (!selectedPage) {
        updateConversationTagsMap({});
        return;
      }

      const externalIds = displayConversations
        .filter((conv) => conv.channel === CHANNELS.FACEBOOK)
        .map((conv) => conv.lastMessage?.conversationId)
        .filter((id): id is string => Boolean(id));

      if (externalIds.length === 0) {
        console.log("Skipping fetch tags - no externalIds");
        updateConversationTagsMap({});
        return;
      }

      const fetchKey = `${conversationsKey}-${activeTab}`;
      if (
        lastFetchedKeyRef.current === fetchKey &&
        lastFetchedKeyRef.current !== ""
      ) {
        return;
      }

      try {
        const tagsMap = await getConversationTagsBatch({
          externalIds: externalIds,
          platform: PLATFORMS.FACEBOOK,
          pageId: selectedPage.id,
        });
        lastFetchedKeyRef.current = fetchKey;
        updateConversationTagsMap(tagsMap);
      } catch (error) {
        console.error("Error fetching conversation tags batch:", error);
      }
    };

    fetchAllTags();
  }, [conversationsKey, selectedFanpageName, facebookPages, activeTab, updateConversationTagsMap, displayConversations]);

  useEffect(() => {
    const fetchAgentsForConversations = async () => {
      if (!selectedFanpageName || displayConversations.length === 0) {
        return;
      }

      const selectedPage = facebookPages.find(
        (page) => page.name === selectedFanpageName
      );

      if (!selectedPage) {
        return;
      }

      const externalIds = displayConversations
        .filter((conv) => conv.channel === CHANNELS.FACEBOOK)
        .map((conv) => conv.lastMessage?.conversationId)
        .filter((id): id is string => Boolean(id));

      if (externalIds.length === 0) {
        return;
      }

      const fetchKey = `${conversationsKey}-${activeTab}`;
      if (
        lastFetchedAgentsKeyRef.current === fetchKey &&
        lastFetchedAgentsKeyRef.current !== ""
      ) {
        return;
      }

      try {
        await dispatch(
          fetchAgentsByConversations({
            externalIds: externalIds,
            platform: PLATFORMS.FACEBOOK,
            pageId: selectedPage.id,
          })
        ).unwrap();
        lastFetchedAgentsKeyRef.current = fetchKey;
      } catch (error) {
        console.error("Error fetching agents by conversations:", error);
      }
    };

    fetchAgentsForConversations();
  }, [
    conversationsKey,
    selectedFanpageName,
    facebookPages,
    dispatch,
    activeTab,
    displayConversations,
  ]);
};
